import { FC, KeyboardEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ButtonBase,
  IconButton,
  InputAdornment,
  Popover,
  TextField,
  Typography,
} from '@mui/material';
import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import CalendarMonthOutlined from '@mui/icons-material/CalendarMonthOutlined';
import ChevronLeftRounded from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import { PickersCalendarHeaderProps } from '@mui/x-date-pickers/PickersCalendarHeader';
import { usePickerTranslations } from '@mui/x-date-pickers/hooks';
import {
  addMonths,
  endOfMonth,
  format,
  isAfter,
  isBefore,
  isSameDay,
  Locale,
  startOfMonth,
  subMonths,
} from 'date-fns';
import {
  pickerLocale,
  toDate,
  VALUE_FORMAT,
} from '../FileExplorer/Filters/FilterDateField/pickerLocale';
import './DateRangeField.scss';

export type DateRangeFieldProps = {
  label: string;
  /** First day of the range, `yyyy-MM-dd`. */
  start: string;
  /** Last day of the range, `yyyy-MM-dd`. */
  end: string;
  /** Called with the new first and last day, `yyyy-MM-dd`, in order. */
  onChange: (start: string, end: string) => void;
  /** Earliest day the calendar offers, `yyyy-MM-dd`. */
  min?: string;
  /** Latest day the calendar offers, `yyyy-MM-dd`. */
  max?: string;
};

const ordered = (a: Date, b: Date): [Date, Date] =>
  isAfter(a, b) ? [b, a] : [a, b];

type RangeDayProps = PickersDayProps & {
  rangeStart: Date | null;
  rangeEnd: Date | null;
  onHover: (day: Date) => void;
};

/** A calendar day, marked as the range's first or last day or one between. */
const RangeDay: FC<RangeDayProps> = ({
  day,
  rangeStart,
  rangeEnd,
  onHover,
  className,
  ...other
}) => {
  const isStart = !!rangeStart && isSameDay(day, rangeStart);
  const isEnd = !!rangeEnd && isSameDay(day, rangeEnd);
  const isBetween =
    !!rangeStart &&
    !!rangeEnd &&
    isAfter(day, rangeStart) &&
    isBefore(day, rangeEnd);
  const classes = ['date-range-field__day', className];
  if (isBetween) classes.push('date-range-field__day--between');
  return (
    <PickersDay
      {...other}
      day={day}
      selected={isStart || isEnd}
      className={classes.filter(Boolean).join(' ')}
      onPointerEnter={() => onHover(day)}
    />
  );
};

const RTL_FLIP = { '[dir="rtl"] &': { transform: 'scaleX(-1)' } };

type RangeHeaderProps = PickersCalendarHeaderProps & {
  /** Which of the two months this heads: the first or the second. */
  side: 'start' | 'end';
  locale: Locale;
};

/**
 * The two months share one header: back on the first month's outer edge,
 * forward on the second's, and the switch to the year list on the first
 * month's title only. Each calendar's own header carried all three, which
 * read as two separate pickers.
 */
const RangeHeader: FC<RangeHeaderProps> = ({
  currentMonth,
  onMonthChange,
  view,
  views,
  onViewChange,
  minDate,
  maxDate,
  labelId,
  side,
  locale,
}) => {
  const translations = usePickerTranslations();
  const title = format(currentMonth, 'LLLL yyyy', { locale });
  const paging = view === 'day';
  const arrow = (direction: -1 | 1) => {
    const target = addMonths(currentMonth, direction);
    const disabled =
      direction < 0
        ? !!minDate && isBefore(endOfMonth(target), minDate)
        : !!maxDate && isAfter(startOfMonth(target), maxDate);
    return (
      <IconButton
        size="small"
        aria-label={
          direction < 0 ? translations.previousMonth : translations.nextMonth
        }
        disabled={!paging || disabled}
        onClick={() => onMonthChange(target)}
      >
        {direction < 0 ? (
          <ChevronLeftRounded sx={RTL_FLIP} />
        ) : (
          <ChevronRightRounded sx={RTL_FLIP} />
        )}
      </IconButton>
    );
  };
  // Holds the place of the arrow the other month has, so both titles centre
  // over their own grid.
  const spacer = <span className="date-range-field__header-spacer" />;

  return (
    <div className="date-range-field__header">
      {side === 'start' ? arrow(-1) : spacer}
      <div className="date-range-field__title" id={labelId}>
        {side === 'start' && views.length > 1 ? (
          <ButtonBase
            className="date-range-field__switch"
            aria-label={translations.calendarViewSwitchingButtonAriaLabel(view)}
            onClick={() => onViewChange?.(view === 'day' ? 'year' : 'day')}
          >
            {title}
            <ArrowDropDown fontSize="small" />
          </ButtonBase>
        ) : (
          title
        )}
      </div>
      {side === 'end' ? arrow(1) : spacer}
    </div>
  );
};

/**
 * A date range in one field. Two months open side by side on the range's first
 * month, and page together. The first day clicked starts a new range and the
 * second ends it, in whichever order they come and in either month, with the
 * days between marked while the pointer moves. Escape or a click outside
 * leaves the range as it was.
 *
 * Like FilterDateField, the field is not typed into: a click, Enter or Space
 * opens the calendar.
 */
export const DateRangeField: FC<DateRangeFieldProps> = ({
  label,
  start,
  end,
  onChange,
  min,
  max,
}) => {
  const { t, i18n } = useTranslation();
  const { locale, localeText } = pickerLocale(i18n.language);
  const fieldRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  // The first day clicked, while the second is awaited.
  const [first, setFirst] = useState<Date | null>(null);
  const [hovered, setHovered] = useState<Date | null>(null);

  const startDate = toDate(start);
  const endDate = toDate(end);
  // While picking, the range runs from the first day to the one under the
  // pointer; otherwise it is the range the field holds.
  const [rangeStart, rangeEnd] = first
    ? ordered(first, hovered ?? first)
    : [startDate, endDate];

  // The month in the first calendar; the second shows the one after it.
  const [shownMonth, setShownMonth] = useState(() =>
    startOfMonth(startDate ?? new Date())
  );
  /** Moves both calendars, given the month calendar `index` moved to. */
  const showMonth = (month: Date, index: number) =>
    setShownMonth(startOfMonth(subMonths(month, index)));

  const openCalendar = () => {
    setShownMonth(startOfMonth(startDate ?? new Date()));
    setOpen(true);
  };

  const close = () => {
    setOpen(false);
    setFirst(null);
    setHovered(null);
  };

  const pick = (day: Date | null, selectedView?: string) => {
    // Picking a year or a month only moves the calendar there.
    if (!day || selectedView !== 'day') return;
    if (!first) {
      setFirst(day);
      return;
    }
    const [from, to] = ordered(first, day);
    onChange(format(from, VALUE_FORMAT), format(to, VALUE_FORMAT));
    close();
  };

  const openOnKey = (event: KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openCalendar();
    }
  };

  const shown =
    startDate && endDate
      ? `${format(startDate, 'P', { locale })} – ${format(endDate, 'P', {
          locale,
        })}`
      : '';

  return (
    <LocalizationProvider
      dateAdapter={AdapterDateFns}
      adapterLocale={locale}
      localeText={localeText}
    >
      <TextField
        ref={fieldRef}
        className="date-range-field"
        label={label}
        value={shown}
        size="small"
        fullWidth
        onClick={openCalendar}
        onKeyDown={openOnKey}
        slotProps={{
          input: {
            readOnly: true,
            endAdornment: (
              <InputAdornment position="end">
                <CalendarMonthOutlined fontSize="small" />
              </InputAdornment>
            ),
          },
          inputLabel: { shrink: true },
        }}
      />
      <Popover
        open={open}
        anchorEl={fieldRef.current}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { role: 'dialog', 'aria-label': label } }}
      >
        <div className="date-range-field__months">
          {[shownMonth, addMonths(shownMonth, 1)].map((month, index) => (
            <DateCalendar
              // Remounted on each move: a calendar's month can only be set
              // when it mounts, and the two must stay a month apart.
              key={`${index}:${month.getTime()}`}
              // No value of its own: the range is drawn on the days instead.
              value={null}
              onChange={(day, _state, view) => pick(day, view)}
              referenceDate={month}
              onMonthChange={(next) => showMonth(next, index)}
              onYearChange={(next) => showMonth(next, index)}
              minDate={toDate(min) ?? undefined}
              maxDate={toDate(max) ?? undefined}
              // The year list is reached from the first month's title only.
              views={index === 0 ? ['year', 'day'] : ['day']}
              openTo="day"
              slots={{
                day: RangeDay as FC<PickersDayProps>,
                calendarHeader: RangeHeader as FC<PickersCalendarHeaderProps>,
              }}
              slotProps={{
                day: {
                  rangeStart,
                  rangeEnd,
                  onHover: setHovered,
                } as Partial<PickersDayProps>,
                calendarHeader: {
                  side: index === 0 ? 'start' : 'end',
                  locale,
                } as Partial<PickersCalendarHeaderProps>,
              }}
            />
          ))}
        </div>
        <Typography className="date-range-field__hint" variant="caption">
          {first ? t('Now pick the last day') : t('Pick the first day')}
        </Typography>
      </Popover>
    </LocalizationProvider>
  );
};
